import { useTranslations } from 'next-intl';
import { TablePrimaryHeader } from '..';
import { ResultsExtraHeader } from '../../..';
import type { LotteryName } from '../../../../../lottery-purchases/types';
import type { ExtraGame } from '../../../../types';

type TableHeadlineProps = {
  isPrimaryTable: boolean;
  lotteryName: LotteryName;
  extraGameId: string;
  drawingDate: string;
  extraGames: ExtraGame[];
};

/**
 * TableHeadline component displays the headline for a lottery prize table.
 *
 * - For the primary table (lotteryId === lotteryName), it renders a heading with the translated "prize_table" label.
 * - For extra games, it renders a ResultsExtraHeader with the appropriate title and drawing date.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.isPrimaryTable - Indicates if this is the main prize table.
 * @param {string} props.lotteryName - The name of the lottery.
 * @param {string} props.extraGameId - The identifier for the extra game.
 * @param {string} props.drawingDate - The date of the lottery draw.
 * @param {ExtraGame[]} props.extraGames - List of extra games for the lottery.
 * @returns {JSX.Element} The rendered headline or extra header for the prize table.
 */
function TableHeadline({ lotteryName, isPrimaryTable, extraGameId, drawingDate, extraGames }: TableHeadlineProps) {
  const namespace = `lotteries.${lotteryName}`;
  const t = useTranslations(namespace);
  const tDate = useTranslations('lotteries.dates');

  return isPrimaryTable ? (
    <TablePrimaryHeader namespace={namespace} />
  ) : (
    <ResultsExtraHeader
      lotteryName={lotteryName}
      title={t(`extra_game_${extraGameId}`)}
      drawingDate={tDate('results_extra_games_date', { date: new Date(drawingDate) })}
      result={extraGames!.filter(game => game.name === extraGameId)[0]!}
    />
  );
}

export { TableHeadline };
